import { supabase } from './supabase'

/**
 * 325 (David, 30 Oct: "add counters to menu nav if someone has an action
 * waiting for them ... also add a counter to the bell").
 *
 * ── ONE READ PER SCREEN, AND THE SHELL OWNS IT ──────────────────────────────
 *
 * `waiting_for_me()` returns every count the shell draws, for the caller only,
 * in one round trip. AppShell remounts on every navigation, so each screen
 * reads it once; nothing polls (the bell's reasoning, AppShell.tsx). A screen
 * that just did something that changes a count — sent a reply, answered a
 * survey — calls `refreshWaiting()`, and the shell on screen reads again.
 *
 * Before this the moderation badge was fetched by nine routes, each for itself,
 * and drawn by none of the others; and the bell counted the 30 rows it had
 * loaded. Two surfaces counting one thing is how a badge starts lying, so the
 * counts live in ONE function and the client only routes them to places.
 *
 * Every number is the caller's own (rule 8, B22).
 */
export type Waiting = {
  /** Unread bell rows — the bell's number. */
  notifications: number
  /** Private questions in your lanes whose turn is the lane's (Needs reply). */
  support: number
  /** Your own private questions where the next word is yours. */
  questions: number
  /** moderation_queue_count(), from the same WHERE. */
  moderation: number
  /** Open photo reviews you may act on. */
  photoReviews: number
  /** Open surveys you were asked and have not answered. */
  surveys: number
  /** Your Star One orders ready to collect. */
  orders: number
}

export const NOTHING_WAITING: Waiting = {
  notifications: 0,
  support: 0,
  questions: 0,
  moderation: 0,
  photoReviews: 0,
  surveys: 0,
  orders: 0,
}

const n = (v: unknown) => (typeof v === 'number' && v > 0 ? Math.floor(v) : 0)

export function parseWaiting(j: unknown): Waiting {
  const r = (j && typeof j === 'object' ? j : {}) as Record<string, unknown>
  return {
    notifications: n(r.notifications),
    support: n(r.support),
    questions: n(r.questions),
    moderation: n(r.moderation),
    photoReviews: n(r.photo_reviews),
    surveys: n(r.surveys),
    orders: n(r.orders),
  }
}

export async function loadWaiting(): Promise<Waiting> {
  const { data, error } = await supabase.rpc('waiting_for_me')
  if (error) throw error
  return parseWaiting(data)
}

/**
 * Which count a NAV destination carries. Routes as NAV writes them (no
 * leading slash). `undefined` = this destination carries no count at all,
 * which is different from 0 (nothing waiting today).
 *
 *   support         the Support inbox (a lane holder's Needs reply)
 *   help            337: your own tickets where the next word is yours
 *   moderation      the moderation queue
 *   photos/gallery  photo reviews (the queue is reached from the Gallery)
 *   surveys         surveys you were asked and have not answered
 *   stars           orders ready to collect (Star One → My orders)
 */
export function waitingFor(w: Waiting, route: string): number | undefined {
  switch (route) {
    case 'support':
      return w.support
    case 'help':
      return w.questions
    case 'moderation':
      return w.moderation
    case 'photos/gallery':
      return w.photoReviews
    case 'surveys':
      return w.surveys
    case 'stars':
      return w.orders
    default:
      return undefined
  }
}

/** The badge text: never more than three characters. */
export const badgeText = (count: number) => (count > 99 ? '99+' : String(count))

const EVENT = 'oneconetec:waiting'

/** Ask the shell on screen to read the counts again. Safe anywhere. */
export function refreshWaiting(): void {
  try {
    if (typeof window !== 'undefined') window.dispatchEvent(new Event(EVENT))
  } catch {
    /* a count a beat late is not worth an error */
  }
}

/** For AppShell: run `fn` whenever somebody calls refreshWaiting(). */
export function onRefreshWaiting(fn: () => void): () => void {
  if (typeof window === 'undefined') return () => undefined
  window.addEventListener(EVENT, fn)
  return () => window.removeEventListener(EVENT, fn)
}
